import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';

import { isEROnly } from 'utils/booking';
import hotelPropType from 'constants/propTypes/hotel';
import MealChangeWarning from 'components/shared/MealChangeWarning';
import { bookingShape } from 'constants/propTypes';
import AlertBox from 'components/ui/AlertBox';
import SegmentSummary from './SegmentSummary';
import productTypes from 'constants/productTypes';
import { StyleHeaderItems, StyleHeaderItem, StyledBody } from '../BookingPathReviewBookingSummary/BookingPathReviewBookingSummary.styles';
import SectionHeaderTitle from '../BookingPathReviewBookingSummary/SectionHeaderTitle';
import SectionHeaderBar from '../BookingPathReviewBookingSummary/SectionHeaderBar';
import {
  isBookingMultiJourney,
  isOnlyCancelationBooking,
  getMultiJourneyLegs,
  passengersPendingCancellationHaveInfants
} from './ReviewBookingSummary.utils';
import { isBookingMiceGroup } from '../BookingPathReviewBookingSummary/BookingPathReviewBookingSummary.utils';
import { StyledAssistancebox } from '../BookingPathReviewBookingSummary/BookingPathReviewBookingSummary.styles';

import { somePassengersEntitledToMeal, someSpecialMeals } from 'utils/meals';

const ReviewBookingSummary = ({
  title,
  currency,
  booking = {},
  hotels = [],
  children,
  cancelExtras,
  showToggle = false,
  isRefundPath = false,
  isExchangePath = false
}) => {
  const { currency: bookingCurrency, reference, pendingCreationLegs, pendingCancellationLegs, isBeingRepriced } = booking;

  const isMiceGroup = isBookingMiceGroup({ details: booking.additionalDetails });
  const multiCancelationBooking = isBookingMultiJourney(booking) && isOnlyCancelationBooking(pendingCancellationLegs, pendingCreationLegs);
  const multiJourneyLegs = multiCancelationBooking ? getMultiJourneyLegs(booking) : [];

  let displayMealWarning = false;
  let displayNotAvailableExtrasWarning = false;
  let displayContactEBSCAssistance = false;

  if (pendingCreationLegs) {
    const sortByDepartureDate = (a, b) => new Date(a.timing.isoDate) - new Date(b.timing.isoDate);
    const departureTime = [...pendingCreationLegs].sort(sortByDepartureDate)[0]?.timing.isoDate;

    const bookingIsEROnly = isEROnly({ outbound: pendingCreationLegs });

    const accBookingItems = [];
    const accClassOfService = [];
    const accMeals = [];

    for (const { bookingItems = [], classOfService, meals = [] } of pendingCreationLegs) {
      accBookingItems.push(...bookingItems);
      accClassOfService.push(classOfService);
      accMeals.push(...meals);
    }

    const passengers = accBookingItems.map((bookingItems) => bookingItems?.passenger);
    const entitledToMeal = somePassengersEntitledToMeal(passengers, undefined, accClassOfService);
    const specialMeals = someSpecialMeals(accMeals);

    displayContactEBSCAssistance =
      pendingCreationLegs.length > 0 &&
      pendingCreationLegs.some(({ extraProducts = [] }) =>
        extraProducts.find(({ provisional, type }) => provisional && type === productTypes.DISABILITY_SUPPORT)
      );

    if (pendingCancellationLegs) {
      displayMealWarning =
        !bookingIsEROnly &&
        entitledToMeal &&
        specialMeals &&
        departureTime &&
        pendingCancellationLegs.some((leg) => leg.meals.length > 0) &&
        moment(departureTime).subtract(2, 'days').isBefore(moment());

      displayNotAvailableExtrasWarning =
        pendingCreationLegs.length > 0 &&
        pendingCancellationLegs.length > 0 &&
        pendingCreationLegs.reduce((acc, leg) => acc + (leg.extraProducts?.length || 0), 0) !==
          pendingCancellationLegs.reduce((acc, leg) => acc + (leg.extraProducts?.length || 0), 0);
    }
  }

  if (isRefundPath && pendingCancellationLegs) {
    displayContactEBSCAssistance =
      pendingCancellationLegs.length > 0 &&
      pendingCancellationLegs.some(({ extraProducts = [] }) =>
        extraProducts.find(({ provisional, type }) => provisional && type === productTypes.DISABILITY_SUPPORT)
      );
  }

  const displayInfantWarning = passengersPendingCancellationHaveInfants({
    additionalDetails: booking.additionalDetails,
    pendingCancellationLegs: pendingCancellationLegs
  });
  const infantWarningMessageId = isExchangePath ? 'reviewBooking.infantExchangeWarning' : 'reviewBooking.infantCancellationWarning';

  const trainMarginDeprecated = hotels.reduce(
    (total, { deprecatedTrainMargin }) => total + deprecatedTrainMargin / pendingCancellationLegs.length,
    0
  );

  const chosenCurrency = currency || bookingCurrency;

  const [isActive, setIsActive] = useState(true);

  return (
    <>
      <SectionHeaderBar>
        {showToggle ? (
          <SectionHeaderTitle titleId={title} isActive={isActive} setIsActive={setIsActive} />
        ) : (
          <FormattedMessage id={title} />
        )}
        <>
          {reference && (
            <StyleHeaderItems data-id="booking-details" className="booking-pnr">
              <FormattedMessage id="searchBooking.pnr" />
              <StyleHeaderItem data-cy="pnr">{reference}</StyleHeaderItem>
            </StyleHeaderItems>
          )}
        </>
      </SectionHeaderBar>

      <StyledBody data-cy="exchange-segment-1" hide={!isActive}>
        {isBeingRepriced && (
          <StyledAssistancebox>
            <AlertBox variant={AlertBox.variants.WARNING}>
              <FormattedMessage id="refunds.repriceWarning" />
            </AlertBox>
          </StyledAssistancebox>
        )}
        {displayContactEBSCAssistance && (
          <StyledAssistancebox>
            <AlertBox variant={AlertBox.variants.WARNING}>
              <FormattedMessage id="reviewBooking.contactEBSCWarning" />
            </AlertBox>
          </StyledAssistancebox>
        )}
        {displayInfantWarning && (
          <StyledAssistancebox>
            <AlertBox variant={AlertBox.variants.WARNING}>
              <FormattedMessage id={infantWarningMessageId} />
            </AlertBox>
          </StyledAssistancebox>
        )}
        {displayNotAvailableExtrasWarning && (
          <StyledAssistancebox>
            <AlertBox variant={AlertBox.variants.WARNING}>
              <FormattedMessage id="journeySummary.notAvailableExtrasWarning" />
            </AlertBox>
          </StyledAssistancebox>
        )}
        {!multiCancelationBooking && reference && (
          <>
            <SegmentSummary
              currency={chosenCurrency}
              pendingCreationLegs={pendingCreationLegs}
              pendingCancellationLegs={pendingCancellationLegs}
              trainMarginDeprecated={trainMarginDeprecated}
              cancelledFirst={isBeingRepriced}
              isMiceGroup={isMiceGroup}
            />
            {cancelExtras}
          </>
        )}

        {multiCancelationBooking &&
          reference &&
          multiJourneyLegs.map((multiJourneyLeg, index) => (
            <>
              <h1>
                <FormattedMessage id="multiJourney.journeyLabelId" values={{ journeyId: index + 1 }} />
              </h1>

              <SegmentSummary
                currency={chosenCurrency}
                pendingCreationLegs={[]}
                pendingCancellationLegs={multiJourneyLeg.legs}
                trainMarginDeprecated={trainMarginDeprecated}
                cancelledFirst={isBeingRepriced}
                isMiceGroup={isMiceGroup}
              />
            </>
          ))}

        {displayMealWarning && (
          <>
            <MealChangeWarning />
            <hr />
          </>
        )}
        {children}
      </StyledBody>
    </>
  );
};

ReviewBookingSummary.propTypes = {
  title: PropTypes.string,
  booking: bookingShape,
  hotels: PropTypes.arrayOf(hotelPropType),
  currency: PropTypes.string,
  children: PropTypes.node,
  cancelExtras: PropTypes.node,
  showToggle: PropTypes.bool,
  isRefundPath: PropTypes.bool,
  isExchangePath: PropTypes.bool
};

export default ReviewBookingSummary;
